"use client"

import { useIsMobile } from "@/app/(site)/_utils/_hooks/useIsMobile"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { useLangCode } from "@/src/utils/locale/useLangCode"
import { t } from "@/translations"
import {
    Children,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactNode
} from "react"
import IconArrowLeft from "@/app/(site)/_components/Atoms/Icons/IconArrowLeft"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"

const TABLET_MEDIA_QUERY = "(min-width: 640px) and (max-width: 1023px)"
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"
const ARROW_HIT_AREA =
    "relative before:absolute before:inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']"

const getScrollBehavior = (): ScrollBehavior => {
    if (typeof window === "undefined") return "smooth"
    return window.matchMedia(REDUCED_MOTION_QUERY).matches ? "auto" : "smooth"
}

const progressWidth = (progress: number) => `${Math.max(20, progress * 80 + 20)}%`

interface GCSimpleCarouselSliderProps {
    children: ReactNode
    itemsPerView?: number
    itemsPerViewMobile?: number
    itemsPerViewTablet?: number
    lastItemOnEdge?: boolean
    showControls?: boolean
    className?: string
    trackClassName?: string
    itemClassName?: string
}

const useIsTablet = (): boolean => {
    const [isTablet, setIsTablet] = useState(false)

    useLayoutEffect(() => {
        const mediaQuery = window.matchMedia(TABLET_MEDIA_QUERY)
        const syncTablet = () => setIsTablet(mediaQuery.matches)

        syncTablet()
        mediaQuery.addEventListener("change", syncTablet)

        return () => {
            mediaQuery.removeEventListener("change", syncTablet)
        }
    }, [])

    return isTablet
}

/**
 * The carousel as it was before GCCarouselSlider gained fixed layouts, the next-card peek
 * and caller-set gaps (POC-2128). Key Features and the product recommender keep their
 * spacing and card widths through it; new modules use GCCarouselSlider.
 */
export default function GCSimpleCarouselSlider({
    children,
    itemsPerView: itemsPerViewProp = 3,
    itemsPerViewMobile,
    itemsPerViewTablet,
    lastItemOnEdge,
    showControls = true,
    className,
    trackClassName,
    itemClassName
}: GCSimpleCarouselSliderProps) {
    const langCode = useLangCode()
    const isMobile = useIsMobile(640)
    const isTablet = useIsTablet()
    const sliderRef = useRef<HTMLDivElement>(null)
    const progressFillRef = useRef<HTMLDivElement>(null)
    const currentIndexRef = useRef(0)
    const scrollProgressRef = useRef(0)
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)

    const childrenArray = Children.toArray(children)
    const totalItems = childrenArray.length
    const isPagedScroll = isMobile || isTablet

    const itemsPerView = isMobile
        ? itemsPerViewMobile || 1
        : isTablet
          ? itemsPerViewTablet || 2
          : itemsPerViewProp || 3

    const shouldShowControls = showControls && totalItems > itemsPerView

    const getSlideStride = useCallback(() => {
        const slider = sliderRef.current
        if (!slider) return 0

        const first = slider.children[0] as HTMLElement | undefined
        const second = slider.children[1] as HTMLElement | undefined
        if (first && second) {
            return second.offsetLeft - first.offsetLeft
        }

        return first?.offsetWidth ?? slider.clientWidth
    }, [])

    const handleScroll = useCallback(() => {
        const slider = sliderRef.current
        if (!slider) return

        const scrollLeft = slider.scrollLeft
        const scrollWidth = slider.scrollWidth
        const clientWidth = slider.clientWidth

        setIsAtStart(scrollLeft === 0)
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)

        const maxScroll = scrollWidth - clientWidth
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
        scrollProgressRef.current = progress

        if (progressFillRef.current) {
            progressFillRef.current.style.width = progressWidth(progress)
        }

        // 1-up slides are < 100% wide so a peek of the next card is visible.
        const stride = getSlideStride()
        if (isPagedScroll && stride > 0) {
            currentIndexRef.current = Math.round(scrollLeft / stride)
        }
    }, [getSlideStride, isPagedScroll])

    const scrollToPage = (direction: "prev" | "next") => {
        const slider = sliderRef.current
        if (!slider) return

        const behavior = getScrollBehavior()

        if (isPagedScroll) {
            const delta = direction === "next" ? 1 : -1
            const newIndex = Math.min(
                totalItems - 1,
                Math.max(0, currentIndexRef.current + delta)
            )
            currentIndexRef.current = newIndex
            slider.scrollTo({
                left: newIndex * getSlideStride(),
                behavior
            })
            return
        }

        const scrollAmount = slider.clientWidth * (1 / itemsPerView)
        slider.scrollBy({
            left: direction === "next" ? scrollAmount : -scrollAmount,
            behavior
        })
    }

    const handleResize = useCallback(() => {
        handleScroll()
    }, [handleScroll])

    useLayoutEffect(() => {
        if (typeof window === "undefined") return

        handleScroll()

        const slider = sliderRef.current
        if (slider) {
            slider.addEventListener("scroll", handleScroll, { passive: true })
        }
        window.addEventListener("resize", handleResize, { passive: true })

        return () => {
            if (slider) {
                slider.removeEventListener("scroll", handleScroll)
            }
            window.removeEventListener("resize", handleResize)
        }
    }, [handleResize, handleScroll, totalItems])

    const calculateItemWidth = () => {
        if (isMobile) {
            if (itemsPerView === 1) {
                return "97%"
            }
            return `calc((100% - ${(itemsPerView - 1) * 12}px) / ${itemsPerView})`
        }
        if (isTablet) {
            if (lastItemOnEdge) {
                return `calc((100% - ${(itemsPerView - 2) * 16}px) / ${itemsPerView})`
            }
            return `calc((100% - ${(itemsPerView - 1) * 16}px) / ${itemsPerView})`
        }
        return `calc((100% - ${(itemsPerView - 1) * 24}px) / ${itemsPerView})`
    }

    const handleTrackKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target instanceof HTMLInputElement) return
        if (event.target instanceof HTMLTextAreaElement) return

        if (event.key === "ArrowLeft") {
            event.preventDefault()
            scrollToPage("prev")
        }
        if (event.key === "ArrowRight") {
            event.preventDefault()
            scrollToPage("next")
        }
    }

    const itemWidth = calculateItemWidth()

    return (
        <div
            className={clsx("relative", className)}
            role="region"
            aria-roledescription="carousel"
            aria-label="Carousel"
        >
            <div
                ref={sliderRef}
                role="region"
                className={clsx(
                    "no-scrollbar flex w-[calc(100%+12px)] snap-x snap-mandatory gap-x-12 overflow-x-scroll scroll-smooth pb-16 motion-reduce:scroll-auto sm:w-[calc(100%+16px)] lg:w-full lg:gap-x-24 lg:pb-32",
                    !lastItemOnEdge && "pr-12 sm:pr-16 lg:pr-0",
                    trackClassName
                )}
                aria-label={t(langCode, "common.carousel")}
                tabIndex={0}
                onKeyDown={handleTrackKeyDown}
            >
                {childrenArray.map((child, index) => (
                    <div
                        key={index}
                        className={clsx("shrink-0 snap-start self-start", itemClassName)}
                        style={{ width: itemWidth }}
                    >
                        {child}
                    </div>
                ))}
            </div>
            {shouldShowControls ? (
                <div className="flex items-center justify-between gap-8">
                    <div
                        className="h-1 w-[70%] overflow-hidden rounded-full bg-subtle lg:w-1/2"
                        role="progressbar"
                        aria-label="Carousel scroll progress"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(scrollProgressRef.current * 100)}
                    >
                        <div
                            ref={progressFillRef}
                            className="h-full bg-main-black"
                            style={{
                                width: progressWidth(scrollProgressRef.current)
                            }}
                        />
                    </div>

                    <div className="flex gap-8 p-5">
                        <button
                            type="button"
                            className={clsx(
                                ARROW_HIT_AREA,
                                "text-main-black",
                                FOCUS_VISIBLE_CLASS,
                                HIT_TARGET_CLASS,
                                isAtStart && "text-main-light-grey-disabled"
                            )}
                            disabled={isAtStart}
                            onClick={() => scrollToPage("prev")}
                            aria-label={t(langCode, "common.previous_item")}
                        >
                            <IconArrowLeft aria-hidden="true" className="h-16 w-16" />
                        </button>
                        <button
                            type="button"
                            className={clsx(
                                ARROW_HIT_AREA,
                                "text-main-black",
                                FOCUS_VISIBLE_CLASS,
                                HIT_TARGET_CLASS,
                                isAtEnd && "text-main-light-grey-disabled"
                            )}
                            disabled={isAtEnd}
                            onClick={() => scrollToPage("next")}
                            aria-label={t(langCode, "common.next_item")}
                        >
                            <IconArrowRight aria-hidden="true" className="h-16 w-16" />
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    )
}
